package fr.manyverse.app.presentation.components

import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier

/**
 * `OutlinedTextField` wrapper that standardizes validation feedback across forms.
 *
 * Wiring rules:
 * - `isError` is driven by [errorText] being non-null.
 * - `supportingText` shows [errorText] when present, otherwise [helperText]. This lets a
 *   neutral hint (e.g. a character counter or a "Required" marker) coexist with an error
 *   that takes priority when validation fails.
 *
 * Use this instead of a bare `OutlinedTextField` so users always understand *why* a value
 * is rejected, rather than facing a silently disabled submit button.
 *
 * @param errorText Localized error message, or null when the value is valid.
 * @param helperText Optional neutral supporting text shown when there is no error.
 */
@Composable
fun ValidatedTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    errorText: String? = null,
    helperText: String? = null,
    placeholder: String? = null,
    enabled: Boolean = true,
    singleLine: Boolean = true,
    minLines: Int = 1,
    maxLines: Int = if (singleLine) 1 else Int.MAX_VALUE,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default
) {
    val supporting: (@Composable () -> Unit)? = when {
        errorText != null -> {
            { Text(errorText) }
        }
        helperText != null -> {
            { Text(helperText) }
        }
        else -> null
    }

    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        label = { Text(label) },
        placeholder = placeholder?.let { { Text(it) } },
        isError = errorText != null,
        supportingText = supporting,
        enabled = enabled,
        singleLine = singleLine,
        minLines = minLines,
        maxLines = maxLines,
        keyboardOptions = keyboardOptions,
        keyboardActions = keyboardActions,
        modifier = modifier.fillMaxWidth()
    )
}
